import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import React, { useCallback, useEffect, useState } from 'react';
import {
  FaBarcode,
  FaCalendarAlt,
  FaCheck,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaInfoCircle,
  FaMoneyBillWave,
  FaTimes,
} from 'react-icons/fa';
import {
  cessaoInfosBoleto,
  gerarBoletoCessao,
  isentarTaxaCessao,
  alterarTipoTaxaCessao,
  cessaoTiposTaxa,
} from '~/Services/Service';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const limparZerosPercentual = texto => {
  if (!texto) return texto;
  return texto.replace(/(\d+)\.0+(%)/g, '$1$2').replace(/(\d+\.\d*?)0+(%)/g, '$1$2');
};

const formatCurrency = value => {
  if (value === null || value === undefined) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatDateToInput = date => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

const ModalBoleto = ({ show, onHide, solicitacao, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [infos, setInfos] = useState(null);
  const [valorTaxa, setValorTaxa] = useState('');
  const [dataVencimento, setDataVencimento] = useState('');
  const [editandoValor, setEditandoValor] = useState(false);
  const [mostrarIsencao, setMostrarIsencao] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [usuarioAprovador, setUsuarioAprovador] = useState('');
  const [senhaAprovador, setSenhaAprovador] = useState('');
  const [showConfirmBoleto, setShowConfirmBoleto] = useState(false);
  const [showConfirmIsencao, setShowConfirmIsencao] = useState(false);
  const [erroIsencao, setErroIsencao] = useState(null);
  const [tipoTaxa, setTipoTaxa] = useState(null);
  const [alterandoTipoTaxa, setAlterandoTipoTaxa] = useState(false);
  const [tiposTaxaOptions, setTiposTaxaOptions] = useState([]);

  const formatarValorTaxa = (valor, fp) => {
    if (valor === null || valor === undefined) return '';
    const num = parseFloat(valor);
    if (fp === 'P') {
      const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2).replace(/\.?0+$/, '');
      return formatted + '%';
    }
    return formatCurrency(num);
  };

  const fetchTiposTaxa = useCallback(async () => {
    try {
      const tipos = await cessaoTiposTaxa();
      if (tipos && Array.isArray(tipos)) {
        setTiposTaxaOptions(
          tipos.map(t => {
            const valFin = formatarValorTaxa(t.valor_fin, t.fp);
            const valQui = formatarValorTaxa(t.valor_qui, t.fp);
            const valores = valFin && valQui ? ` (Fin: ${valFin} / Qui: ${valQui})` : '';
            return {
              value: t.codigo,
              label: `${t.descricao}${valores}`,
            };
          })
        );
      }
    } catch (error) {
      console.error('Erro ao buscar tipos de taxa:', error);
    }
  }, []);

  const fetchInfosBoleto = useCallback(async () => {
    if (!solicitacao) return;

    setLoading(true);
    try {
      const response = await cessaoInfosBoleto(solicitacao.codigo);
      setInfos(response);
      setValorTaxa(response.valor_taxa_apurado || 0);
      setTipoTaxa(response.taxa_boleto_codigo_taxa || null);
      setDataVencimento(formatDateToInput(response.data_vencimento_padrao));
    } catch (error) {
      console.error('Erro ao buscar informações do boleto:', error);
      toast.error('Erro ao carregar informações da taxa');
    } finally {
      setLoading(false);
    }
  }, [solicitacao]);

  useEffect(() => {
    if (show && solicitacao) {
      fetchInfosBoleto();
      fetchTiposTaxa();
      setEditandoValor(false);
      setMostrarIsencao(false);
      setJustificativa('');
      setUsuarioAprovador('');
      setSenhaAprovador('');
      setDataVencimento('');
      setShowConfirmBoleto(false);
      setShowConfirmIsencao(false);
      setTipoTaxa(null);
      setAlterandoTipoTaxa(false);
    }
  }, [show, solicitacao, fetchInfosBoleto, fetchTiposTaxa]);

  const handleConfirmGerarBoleto = () => {
    if (!valorTaxa || parseFloat(valorTaxa) <= 0) {
      toast.error('O valor da taxa deve ser maior que zero');
      return;
    }

    if (!dataVencimento) {
      toast.error('A data de vencimento é obrigatória');
      return;
    }

    setShowConfirmBoleto(true);
  };

  const handleGerarBoleto = async () => {
    setShowConfirmBoleto(false);
    setSubmitting(true);
    try {
      await gerarBoletoCessao({
        codigo_solicitacao: solicitacao.codigo,
        valor_taxa: parseFloat(valorTaxa),
        data_vencimento: dataVencimento,
        observacao: '',
      });
      toast.success('Boleto gerado com sucesso!');
      onHide();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Erro ao gerar boleto:', error);
      toast.error(error ? error.message : 'Erro ao gerar boleto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmIsentar = () => {
    if (!justificativa || justificativa.trim().length < 10) {
      toast.error('A justificativa deve ter no mínimo 10 caracteres');
      return;
    }

    if (!usuarioAprovador || !usuarioAprovador.trim()) {
      toast.error('Informe o usuário do aprovador');
      return;
    }

    if (!senhaAprovador) {
      toast.error('Informe a senha do aprovador');
      return;
    }

    setShowConfirmIsencao(true);
  };

  const handleIsentar = async () => {
    setShowConfirmIsencao(false);
    setSubmitting(true);
    try {
      await isentarTaxaCessao({
        codigo_solicitacao: solicitacao.codigo,
        valor_taxa: parseFloat(valorTaxa),
        justificativa: justificativa.trim(),
        usuario_aprovador: usuarioAprovador.trim(),
        senha_aprovador: senhaAprovador,
      });
      toast.success('Taxa isenta com sucesso!');
      onHide();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Erro ao isentar taxa:', error);
      const mensagem = error ? error.message : 'Erro ao isentar taxa';
      if (mensagem && mensagem.includes('Dados incompletos no contrato')) {
        setErroIsencao(mensagem);
      } else {
        toast.error(mensagem);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAlterarTipoTaxa = async codigoTaxa => {
    if (!solicitacao || !codigoTaxa) return;
    setAlterandoTipoTaxa(true);
    try {
      await alterarTipoTaxaCessao({
        codigo_solicitacao: solicitacao.codigo,
        codigo_taxa: codigoTaxa,
      });
      setTipoTaxa(codigoTaxa);
      toast.success('Tipo da taxa alterado com sucesso!');
      fetchInfosBoleto();
    } catch (error) {
      console.error('Erro ao alterar tipo da taxa:', error);
      toast.error(error ? error.message : 'Erro ao alterar tipo da taxa');
    } finally {
      setAlterandoTipoTaxa(false);
    }
  };

  const clienteJaIsento = infos ? infos.cliente_isento : false;
  const podeGerarBoleto = infos
    ? infos.pode_transferir && !infos.taxa_ja_emitida
    : false;
  const podeIsentar = infos
    ? infos.pode_isentar && !infos.taxa_ja_emitida
    : false;

  if (!solicitacao) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaFileInvoiceDollar className="mr-2" />
          Taxa de Transferência - Solicitação #{solicitacao.codigo}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#f8f9fa' }}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Carregando informações...</p>
          </div>
        ) : infos ? (
          <>
            <Card className="mb-3">
              <Card.Header className="py-2 bg-light">
                <strong>
                  <FaInfoCircle className="mr-2" />
                  Informações do Imóvel
                </strong>
              </Card.Header>
              <Card.Body className="py-2">
                <Row>
                  <Col md={6}>
                    <small className="text-muted">Empreendimento</small>
                    <p className="mb-1 font-weight-bold">
                      {infos.empreendimento}
                    </p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">Quadra</small>
                    <p className="mb-1">{infos.quadra}</p>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted">Lote</small>
                    <p className="mb-1">{infos.lote}</p>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <small className="text-muted">Cliente</small>
                    <p className="mb-0">{infos.nome_cliente}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header className="py-2 bg-light">
                <strong>
                  <FaFileInvoiceDollar className="mr-2" />
                  Tipo da Taxa de Cessão
                </strong>
              </Card.Header>
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={6}>
                    <Form.Group className="mb-0">
                      <Form.Label>Tipo da Taxa</Form.Label>
                      <Select
                        options={tiposTaxaOptions}
                        value={tiposTaxaOptions.find(o => o.value === tipoTaxa) || null}
                        onChange={opt => {
                          if (opt && opt.value !== tipoTaxa) {
                            handleAlterarTipoTaxa(opt.value);
                          }
                        }}
                        styles={{
                          ...customStyles,
                          menuPortal: base => ({ ...base, zIndex: 9999 }),
                        }}
                        components={{ DropdownIndicator }}
                        placeholder="Selecione o tipo..."
                        menuPortalTarget={document.body}
                        isDisabled={
                          submitting ||
                          alterandoTipoTaxa ||
                          !podeGerarBoleto
                        }
                        isLoading={alterandoTipoTaxa}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <div className="text-center p-3 bg-light rounded">
                      <small className="text-muted d-block">
                        Tipo Atual
                      </small>
                      <h5 className="mb-0 text-primary">
                        {limparZerosPercentual(infos.descricao_taxa) || '-'}
                      </h5>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {infos.cliente_isento && (
              <Alert variant="info" className="mb-3">
                <FaInfoCircle className="mr-2" />
                Contrato possui isenção da Taxa de Cessão cadastrada em sistema.
              </Alert>
            )}

            {infos.motivos_bloqueio && infos.motivos_bloqueio.length > 0 && (
              <Alert variant="danger" className="mb-3">
                <FaExclamationTriangle className="mr-2" />
                <strong>Bloqueios encontrados:</strong>
                <ul className="mb-0 mt-2">
                  {infos.motivos_bloqueio.map((motivo, idx) => (
                    <li key={idx}>{motivo}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {infos.taxa_ja_emitida && (
              <Alert variant="warning" className="mb-3">
                <FaExclamationTriangle className="mr-2" />
                <strong>Taxa já emitida!</strong> Esta solicitação já possui um
                boleto de taxa de transferência gerado.
              </Alert>
            )}


            <Card className="mb-3">
              <Card.Header className="py-2 bg-light">
                <strong>
                  <FaCalendarAlt className="mr-2" />
                  Data de Vencimento do Boleto
                </strong>
              </Card.Header>
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={6}>
                    <Form.Group className="mb-0">
                      <Form.Label>Data de Vencimento</Form.Label>
                      <Form.Control
                        type="date"
                        value={dataVencimento}
                        onChange={e => setDataVencimento(e.target.value)}
                        disabled={submitting || !podeGerarBoleto}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <Form.Text className="text-muted">
                        Por padrão, o vencimento é calculado como o próximo dia
                        útil.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <div className="text-center p-3 bg-light rounded">
                      <small className="text-muted d-block">
                        Vencimento Selecionado
                      </small>
                      <h5 className="mb-0 text-primary">
                        {dataVencimento
                          ? new Date(
                              dataVencimento + 'T00:00:00'
                            ).toLocaleDateString('pt-BR')
                          : '-'}
                      </h5>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header className="py-2 bg-light">
                <strong>
                  <FaMoneyBillWave className="mr-2" />
                  Valor da Taxa de Transferência
                </strong>
              </Card.Header>
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={6}>
                    <Form.Group className="mb-0">
                      <Form.Label>Valor Apurado</Form.Label>
                      <InputGroup>
                        <InputGroup.Prepend>
                          <InputGroup.Text>R$</InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          value={valorTaxa}
                          onChange={e => setValorTaxa(e.target.value)}
                          disabled={!editandoValor || submitting}
                          className={editandoValor ? 'border-primary' : ''}
                        />
                        <InputGroup.Append>
                          <Button
                            variant={
                              editandoValor ? 'success' : 'outline-secondary'
                            }
                            onClick={() => setEditandoValor(!editandoValor)}
                            disabled={submitting || !podeGerarBoleto}
                          >
                            {editandoValor ? <FaCheck /> : 'Editar'}
                          </Button>
                        </InputGroup.Append>
                      </InputGroup>
                      {editandoValor && (
                        <Form.Text className="text-warning">
                          Alteração de valor requer autorização especial.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <div className="text-center p-3 bg-light rounded">
                      <small className="text-muted d-block">
                        Valor a Pagar
                      </small>
                      <h3 className="mb-0 text-primary">
                        {formatCurrency(parseFloat(valorTaxa) || 0)}
                      </h3>
                    </div>
                  </Col>
                </Row>

                {infos.descricao_taxa && (
                  <div className="mt-2">
                    <Badge variant="secondary">
                      Tipo: {limparZerosPercentual(infos.descricao_taxa)}
                    </Badge>
                  </div>
                )}
              </Card.Body>
            </Card>

            {mostrarIsencao && podeIsentar && (
              <Card className="mb-3 border-warning">
                <Card.Header className="py-2 bg-warning text-dark">
                  <strong>Isenção da Taxa de Transferência</strong>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Justificativa <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={justificativa}
                      onChange={e => setJustificativa(e.target.value)}
                      placeholder="Informe o motivo da isenção (mínimo 10 caracteres)..."
                      disabled={submitting}
                      maxLength={3000}
                    />
                    <Form.Text className="text-muted">
                      {justificativa.length}/3000 caracteres
                    </Form.Text>
                  </Form.Group>

                  <Alert variant="info" className="mb-3">
                    <FaInfoCircle className="mr-2" />
                    <strong>Autorização Obrigatória:</strong> A isenção de taxa
                    requer aprovação de um usuário autorizado.
                  </Alert>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Usuario do Aprovador{' '}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={usuarioAprovador}
                          onChange={e => setUsuarioAprovador(e.target.value)}
                          placeholder="Digite o usuário..."
                          disabled={submitting}
                          autoComplete="off"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Senha do Aprovador{' '}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          value={senhaAprovador}
                          onChange={e => setSenhaAprovador(e.target.value)}
                          placeholder="Digite a senha..."
                          disabled={submitting}
                          autoComplete="new-password"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {!submitting &&
                    (justificativa.trim().length < 10 ||
                      !usuarioAprovador.trim() ||
                      !senhaAprovador) && (
                      <div className="mb-2">
                        {justificativa.trim().length < 10 && (
                          <small className="text-danger d-block">
                            <FaTimes size={10} className="mr-1" />
                            A justificativa deve ter no mínimo 10 caracteres ({justificativa.trim().length}/10)
                          </small>
                        )}
                        {!usuarioAprovador.trim() && (
                          <small className="text-danger d-block">
                            <FaTimes size={10} className="mr-1" />
                            Informe o usuário do aprovador
                          </small>
                        )}
                        {!senhaAprovador && (
                          <small className="text-danger d-block">
                            <FaTimes size={10} className="mr-1" />
                            Informe a senha do aprovador
                          </small>
                        )}
                      </div>
                    )}

                  <div className="d-flex justify-content-end">
                    <Button
                      variant="outline-secondary"
                      className="mr-2"
                      onClick={() => {
                        setMostrarIsencao(false);
                        setJustificativa('');
                        setUsuarioAprovador('');
                        setSenhaAprovador('');
                      }}
                      disabled={submitting}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="warning"
                      onClick={handleConfirmIsentar}
                      disabled={
                        submitting ||
                        justificativa.trim().length < 10 ||
                        !usuarioAprovador.trim() ||
                        !senhaAprovador
                      }
                    >
                      {submitting ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="mr-2"
                          />
                          Processando...
                        </>
                      ) : (
                        'Confirmar Isenção'
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3">
              <Card.Header className="py-2 bg-light">
                <strong>Status das Validações</strong>
              </Card.Header>
              <Card.Body className="py-2">
                <Row>
                  <Col md={4} className="text-center border-right">
                    <div
                      className={`p-2 rounded ${
                        !infos.tem_renegociacao
                          ? 'bg-success-light'
                          : 'bg-danger-light'
                      }`}
                    >
                      {!infos.tem_renegociacao ? (
                        <FaCheck className="text-success" />
                      ) : (
                        <FaTimes className="text-danger" />
                      )}
                      <small className="d-block mt-1">Renegociação</small>
                    </div>
                  </Col>
                  <Col md={4} className="text-center border-right">
                    <div
                      className={`p-2 rounded ${
                        !infos.tem_cldb ? 'bg-success-light' : 'bg-danger-light'
                      }`}
                    >
                      {!infos.tem_cldb ? (
                        <FaCheck className="text-success" />
                      ) : (
                        <FaTimes className="text-danger" />
                      )}
                      <small className="d-block mt-1">CLDB</small>
                    </div>
                  </Col>
                  <Col md={4} className="text-center">
                    <div
                      className={`p-2 rounded ${
                        !infos.tem_titulos_abertos
                          ? 'bg-success-light'
                          : 'bg-danger-light'
                      }`}
                    >
                      {!infos.tem_titulos_abertos ? (
                        <FaCheck className="text-success" />
                      ) : (
                        <FaTimes className="text-danger" />
                      )}
                      <small className="d-block mt-1">Títulos Abertos</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </>
        ) : (
          <Alert variant="warning">
            Não foi possível carregar as informações da taxa de transferência.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={submitting}
        >
          Fechar
        </Button>
        {podeIsentar && !mostrarIsencao && (
          <Button
            variant="warning"
            onClick={() => setMostrarIsencao(true)}
            disabled={submitting || editandoValor}
          >
            Isentar Taxa
          </Button>
        )}
        {podeGerarBoleto &&
          !mostrarIsencao &&
          !submitting &&
          (editandoValor ||
            !valorTaxa ||
            parseFloat(valorTaxa) <= 0 ||
            !dataVencimento) && (
            <div className="d-flex flex-column align-items-end mr-2">
              {editandoValor && (
                <small className="text-danger">
                  <FaTimes size={10} className="mr-1" />
                  Finalize a edição do valor
                </small>
              )}
              {(!valorTaxa || parseFloat(valorTaxa) <= 0) && (
                <small className="text-danger">
                  <FaTimes size={10} className="mr-1" />
                  O valor da taxa deve ser maior que zero
                </small>
              )}
              {!dataVencimento && (
                <small className="text-danger">
                  <FaTimes size={10} className="mr-1" />
                  Informe a data de vencimento
                </small>
              )}
            </div>
          )}
        {podeGerarBoleto && !mostrarIsencao && (
          <Button
            variant="success"
            onClick={handleConfirmGerarBoleto}
            disabled={
              submitting ||
              editandoValor ||
              !valorTaxa ||
              parseFloat(valorTaxa) <= 0 ||
              !dataVencimento
            }
          >
            {submitting ? (
              <>
                <Spinner size="sm" animation="border" className="mr-2" />
                Processando...
              </>
            ) : (
              <>
                <FaBarcode className="mr-2" />
                Gerar Boleto
              </>
            )}
          </Button>
        )}
      </Modal.Footer>

      <Modal
        show={showConfirmBoleto}
        onHide={() => setShowConfirmBoleto(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <FaBarcode className="mr-2" />
            Confirmar Geração do Boleto
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clienteJaIsento && (
            <Alert variant="warning" className="mb-3">
              <FaExclamationTriangle className="mr-2" />
              <strong>O cliente possui Isenção da Taxa de Transferência</strong>, deseja prosseguir com a emissão do boleto mesmo assim?
            </Alert>
          )}
          <p>Você está prestes a gerar um boleto com os seguintes dados:</p>
          <div className="bg-light p-3 rounded mb-3">
            <Row>
              <Col xs={6}>
                <small className="text-muted d-block">Valor da Taxa</small>
                <strong className="text-success">
                  {formatCurrency(parseFloat(valorTaxa) || 0)}
                </strong>
              </Col>
              <Col xs={6}>
                <small className="text-muted d-block">Data de Vencimento</small>
                <strong>
                  {dataVencimento
                    ? new Date(dataVencimento + 'T00:00:00').toLocaleDateString(
                        'pt-BR'
                      )
                    : '-'}
                </strong>
              </Col>
            </Row>
          </div>
          <Alert variant="warning" className="mb-0">
            <FaExclamationTriangle className="mr-2" />
            <strong>Atenção:</strong> Esta ação não poderá ser desfeita. Deseja
            continuar?
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowConfirmBoleto(false)}
          >
            Cancelar
          </Button>
          <Button variant="success" onClick={handleGerarBoleto}>
            <FaCheck className="mr-2" />
            Confirmar e Gerar Boleto
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmIsencao}
        onHide={() => setShowConfirmIsencao(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title>
            <FaExclamationTriangle className="mr-2" />
            Confirmar Isenção da Taxa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Você está prestes a isentar a taxa de transferência:</p>
          <div className="bg-light p-3 rounded mb-3">
            <Row className="mb-2">
              <Col xs={12}>
                <small className="text-muted d-block">Valor a ser Isento</small>
                <strong className="text-warning">
                  {formatCurrency(parseFloat(valorTaxa) || 0)}
                </strong>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <small className="text-muted d-block">Justificativa</small>
                <span>{justificativa}</span>
              </Col>
            </Row>
          </div>
          <Alert variant="danger" className="mb-0">
            <FaExclamationTriangle className="mr-2" />
            <strong>Atenção:</strong> A isenção será registrada e não poderá ser
            desfeita. Deseja continuar?
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowConfirmIsencao(false)}
          >
            Cancelar
          </Button>
          <Button variant="warning" onClick={handleIsentar}>
            <FaCheck className="mr-2" />
            Confirmar Isenção
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!erroIsencao}
        onHide={() => setErroIsencao(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="mr-2 text-danger" />
            Erro ao Isentar Taxa — Dados Incompletos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <FaInfoCircle className="mr-2" />
            <strong>Os dados do contrato estão incompletos no cadastro.</strong>{' '}
            Verifique os itens abaixo no GES e corrija antes de tentar novamente.
          </Alert>
          <div className="bg-light p-3 rounded">
            {erroIsencao &&
              erroIsencao
                .replace(/^.*Dados incompletos no contrato \(Boleto \d+\): /, '')
                .split(' | ')
                .map((problema, idx) => (
                  <div key={idx} className="mb-2">
                    <Badge variant="danger" className="mr-2">
                      {idx + 1}
                    </Badge>
                    {problema}
                  </div>
                ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setErroIsencao(null)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default ModalBoleto;
